import { Dialog, DialogActions, DialogContent, FormHelperText, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { CustomDialogTitle } from "../../../../Common/Custom/CustomDialogTitle";
import { SkeletonLoading } from "../../../../../Component/Common/SkeletonLoading/SkeletonLoading";
import { useEffect, useState } from "react";
import { StipendDto } from "../../../../../Component/Model/Dto/WSP/MerSETASDE";
import { Months } from "../../../../../Component/Model/Enum/BEE123Enums";
import { MDButton } from "../../../../../Component/Common/Components/MDButton";
import { LoadingButton } from "@mui/lab";
import { StipendService } from "../../../../../Component/Api/Attendee/StipendService";
import { toast } from "react-toastify";
import { formatCurrencyInputValue } from "../../../../../Helpers/CurrencyFormatter";
import { LongTableDateHelper, isNullOrEmpty } from "../../../../../Component/Model/helpers";

interface IProps {
    attendee: SelectedAttendee;
    startDate: Date;
    endDate: Date;
    merSetaId: number;
    handleClose: () => void;
    showModal: boolean;
}

interface SelectedAttendee {
    name: string;
    platformPeopleId: string;
}

export const StipendsModal = (props: IProps) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [stipendData, setStipendData] = useState<StipendDto[]>([]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await StipendService.GetStipendsForAttendee(props.attendee.platformPeopleId, props.merSetaId);
            const dataFromServer = response.data;

            const startYear = new Date(props.startDate).getFullYear();
            const endYear = new Date(props.endDate).getFullYear();
            const startMonth = new Date(props.startDate).getMonth() + 1;
            const endMonth = new Date(props.endDate).getMonth() + 1;

            const allMonthsData = [];

            let currentYear = startYear;

            for (let monthId = startMonth; currentYear < endYear || (currentYear === endYear && monthId <= endMonth);) {
                allMonthsData.push({
                    year: currentYear,
                    monthId: monthId,
                    cost: null,
                });
                if (monthId === 12) {
                    currentYear++;
                    monthId = 1;
                } else {
                    monthId++;
                }
            }

            const mergedData = allMonthsData.map((monthData) => {
                const matchingData = dataFromServer.find((serverData) => serverData.monthId === monthData.monthId);
                return matchingData ? matchingData : monthData;
            });

            setStipendData(mergedData);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            let stipendsWithCost = stipendData.map((item) => {
                const costString = item.cost?.toString();
                const numericValue = costString?.replace(/[^0-9.,]/g, '')?.replace(',', '.');
                const parsedCost = isNullOrEmpty(numericValue) ? null : parseFloat(numericValue!);

                return {
                    id: item.id,
                    attendeeId: item.attendeeId,
                    monthId: item.monthId,
                    year: item.year,
                    cost: parsedCost
                }
            });

            stipendsWithCost = stipendsWithCost.filter((item) => !isNullOrEmpty(item.cost));

            await StipendService.SaveStipends(stipendsWithCost, props.attendee.platformPeopleId, props.merSetaId);
            toast.success(`Successfully saved stipends`);
            props.handleClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleStipendCostChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, rowId: number | undefined) => {
        if (rowId !== undefined) {
            if (!isNullOrEmpty(event.target.value)) {
                const numericValue = event.target.value.replace(/[^0-9.,\,,\.,\s]/g, '');
                const formattedValue = formatCurrencyInputValue(numericValue, 2);

                const updatedData = stipendData.map((item) =>
                    item.monthId === rowId ? { ...item, cost: formattedValue } : item
                );
                setStipendData(updatedData);
            } else {
                const updatedData = stipendData.map((item) =>
                    item.monthId === rowId ? { ...item, cost: null } : item
                );
                setStipendData(updatedData);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Dialog open={props.showModal} onClose={props.handleClose} aria-labelledby="form-dialog-title" maxWidth='md' fullWidth>
            <CustomDialogTitle id="customized-dialog-title" onClose={props.handleClose}>
                Capture Stipend
            </CustomDialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        The duration of this training intervention is from {LongTableDateHelper(props.startDate.toString())} - {LongTableDateHelper(props.endDate.toString())}.
                        Capture stipend cost per month for {props.attendee.name}.
                    </Grid>
                    {isLoading ?
                        <SkeletonLoading formLoading FieldAmount={6} width="100%" />
                        :
                        <Grid item xs={12}>
                            <table style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: "var(--semantics-disabled, #4d4f53)", height: '40px' }}>
                                        <th><Typography variant='body2' fontWeight="700" ml={'7px'} color='#FFF'>Month</Typography></th>
                                        <th><Typography variant='body2' fontWeight="700" ml={'7px'} color='#FFF'>Stipend Cost (excl. VAT)</Typography></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stipendData.map((row) => (
                                        <tr key={row.id} style={{ borderBottom: '1px solid #C9C9C980', height: '40px' }}>
                                            <td><Typography variant='body2' ml={'7px'}> {Months[row.monthId]} {row.year} </Typography></td>
                                            <td>
                                                <TextField
                                                    label={" "}
                                                    sx={{
                                                        padding: '3px',
                                                        '& .MuiInputLabel-shrink': {
                                                            transform: 'none',
                                                        },
                                                    }}
                                                    value={formatCurrencyInputValue(row.cost, 2)}
                                                    onChange={(e) => {
                                                        handleStipendCostChanged(e, row.monthId);
                                                    }}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">R</InputAdornment>,
                                                    }}
                                                    InputLabelProps={{
                                                        shrink: false
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Grid>

                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <LoadingButton color='primary' variant='contained' onClick={handleSave} loading={isSaving}>
                    Save
                </LoadingButton>
                <MDButton color='primary' variant='outlined' onClick={props.handleClose}>
                    Cancel
                </MDButton>
            </DialogActions>
        </Dialog>
    )
}